import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Row, Col, Form, FloatingLabel, Card } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PhoneVerification from "../../components/phoneVerificationForm";
import { useNotification } from "../../components/notification";

// Define validation schema using yup
const schema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup
    .string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must be at most 20 characters")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+{}|:"<>?~`\-=[\]\\;',./])/,
      "Password must contain at least one letter, one number, and one special character"
    ),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  phone: yup
    .string()
    .required("Phone is required")
    .matches(/^\d{10}$/, "Phone must be 10 digits"),
  phoneVerified: yup.boolean().default(false).required("Phone verification is required"),
  address: yup.object().shape({
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    address1: yup.string().required("Address line 1 is required"),
    address2: yup.string().optional(),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    postalCode: yup
      .string()
      .required("Postal code is required")
      .matches(/^\d{6}$/, "Postal code must be 6 digits"),
    country: yup.string().required("Country is required"),
    phone: yup
      .string()
      .required("Phone is required")
      .matches(/^\d{10}$/, "Phone must be 10 digits"),
    gstNumber: yup.string().notRequired(), // Optional field
    type: yup.string().required("Address type is required"),
    isDefault: yup.boolean().default(true),
    phoneVerified: yup.boolean().default(false).required("Phone verification is required"),
  }),
});
const CreateAccount = forwardRef(({ onSubmit }, ref) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm({
    resolver: yupResolver(schema), // Integrate yup with react-hook-form
  });

  const [phone, setPhone] = useState("");
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [addressPhone, setAddressPhone] = useState("");
  const [isAddressPhoneVerified, setIsAddressPhoneVerified] = useState(false);
  const { showNotification } = useNotification();
  // useImperativeHandle(ref, () => ({
  //   submitForm: handleSubmit(onSubmit),
  // }));

  //Expose the handleSubmit function to the parent component
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (!isPhoneVerified || !isAddressPhoneVerified) {
        if (!isPhoneVerified) {
          showNotification("danger", "Phone number must be verified.", 5000);
          return;
        }
       else if (!isAddressPhoneVerified && isPhoneVerified) {
          showNotification("danger", "Address phone number must be verified.", 5000);
          return;
        }
      }      
      setValue("phoneVerified", isPhoneVerified);
      setValue("address.phoneVerified", isAddressPhoneVerified);
      handleSubmit(onSubmit)();
    },
    isFormValid: () => {
      let isPhoneValid = phone && isPhoneVerified;
      let isAddressPhoneValid = addressPhone && isAddressPhoneVerified;
      setValue("phoneVerified", isPhoneValid);
      setValue("address.phoneVerified", isAddressPhoneValid);
      return isPhoneValid && isAddressPhoneValid;
    },
  }));

  return (
    <Card className="p-4 border-0">
      <Card.Body>
        <h1 className="mb-2 fs-5">Checkout</h1>
        <p className="mb-4 text-light-emphasis fw-light">
          Please enter your details and shipping address.
        </p>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <h6 className="mb-3">Billing Details</h6>
          <Row>
            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtEmail"
                label="Email"
                className="mb-3"
              >
                <Form.Control
                  type="email"
                  placeholder="Email"
                  {...register("email")}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtPassword"
                label="Password"
                className="mb-3"
              >
                <Form.Control
                  type="password"
                  placeholder="Password"
                  {...register("password")}
                  isInvalid={!!errors.password}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtFirstName"
                label="First Name"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="First Name"
                  {...register("firstName")}
                  isInvalid={!!errors.firstName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtLastName"
                label="Last Name"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Last Name"
                  {...register("lastName")}
                  isInvalid={!!errors.lastName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lastName?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <PhoneVerification
                value={phone}
                onChange={(val) => {
                  setPhone(val);
                  setValue("phone", val);
                }}
                onVerified={setIsPhoneVerified}
                disabled={isSubmitting}
                id="phone"
              />
              <input
                type="hidden"
                value={phone}
                {...register("phone")}
              />
            </Col>
            <Col md={6}>
              <FloatingLabel
                controlId="txtGstNumber"
                label="GST Number (Optional)"
                className="mb-4"
              >
                <Form.Control
                  type="text"
                  placeholder="GST Number"
                  {...register("gstNumber")}
                />
              </FloatingLabel>
            </Col>
          </Row>
          {/* Address Details */}
          <h6 className="mb-3">Shipping Address</h6>
          <Row>
            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtAddressFirstName"
                label="First Name"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="First Name"
                  {...register("address.firstName")}
                  isInvalid={!!errors.address?.firstName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.firstName?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtAddressLastName"
                label="Last Name"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Last Name"
                  {...register("address.lastName")}
                  isInvalid={!!errors.address?.lastName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.lastName?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtAddress1"
                label="Address Line 1"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Address Line 1"
                  {...register("address.address1")}
                  isInvalid={!!errors.address?.address1}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.address1?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtAddress2"
                label="Address Line 2"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Address Line 2"
                  {...register("address.address2")}
                  isInvalid={!!errors.address?.address2}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.address2?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <FloatingLabel controlId="txtCity" label="City" className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="City"
                  {...register("address.city")}
                  isInvalid={!!errors.address?.city}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.city?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtState"
                label="State"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="State"
                  {...register("address.state")}
                  isInvalid={!!errors.address?.state}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.state?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtPostalCode"
                label="Postal Code"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Postal Code"
                  {...register("address.postalCode")}
                  isInvalid={!!errors.address?.postalCode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.postalCode?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtCountry"
                label="Country"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Country"
                  {...register("address.country")}
                  isInvalid={!!errors.address?.country}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.country?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            {/* <Col md={6} className="mb-3">
              <FloatingLabel
                controlId="txtAddressPhone"
                label="Phone"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Phone"
                  {...register("address.phone")}
                  isInvalid={!!errors.address?.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.phone?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col> */}
            <Col md={6} className="mb-3">
              <PhoneVerification
                value={addressPhone}
                onChange={(val) => {
                  setAddressPhone(val);
                  if (isPhoneVerified && val === phone){
                    setIsAddressPhoneVerified(true);
                  }
                  setValue("address.phone", val);
                }}
                onVerified={setIsAddressPhoneVerified}  
                existingPhoneNumber={phone}
                isAlreadyVerified={isPhoneVerified}
                disabled={isSubmitting}
                id="addressPhone"
              />
              <input
                type="hidden"
                value={addressPhone}
                {...register("address.phone")}
              />
            </Col>
            <Col md={6}>
              <FloatingLabel
                controlId="ddAddressType"
                label="Address Type"
                className="mb-3"
              >
                <Form.Select
                  {...register("address.type")}
                  isInvalid={!!errors.address?.type}
                >
                  <option value="">Choose...</option>
                  <option value="shipping">Shipping</option>
                  <option value="billing">Billing</option>
                  <option value="both">Both</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.address?.type?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
});

export default CreateAccount;
